/** Natal 2026 — peças de tela usadas por todas as abas (sem regra de negócio). */
import { useMemo, useState, type ReactNode } from 'react'
import { Box, Button, Chip, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Typography } from '@mui/material'
import { horaSp, motivoOutro, ordenar, pct, STATUS } from './logic'
import type { Anuncio, StatusRitmo } from './types'

export type Coluna<T> = { label: string; valor?: (l: T) => number | string | null; render: (l: T) => ReactNode; alinhar?: 'left' | 'right' | 'center' }

/** Tabela com todas as colunas ordenáveis (clique no título: maior → menor; de novo inverte).
 * Tabela longa rola dentro do próprio quadro (até `alturaMax`) com o cabeçalho fixo no topo;
 * `alturaMax={null}` desliga o limite (tabela dentro de outra que já rola). */
export function TabelaOrdenavel<T>({ colunas, linhas, vazio, onLinha, alturaMax = '70vh' }: {
  colunas: Coluna<T>[]; linhas: T[]; vazio: string; onLinha?: (l: T) => void; alturaMax?: string | null
}) {
  const [idx, setIdx] = useState<number | null>(null)
  const [dir, setDir] = useState<'asc' | 'desc'>('desc')
  const lista = useMemo(() => {
    const v = idx != null ? colunas[idx].valor : undefined
    return v ? ordenar(linhas, v, dir) : linhas
  }, [colunas, linhas, idx, dir])
  if (!linhas.length) return <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>{vazio}</Typography>
  return (
    <Box sx={{ overflow: 'auto', maxHeight: alturaMax ?? undefined }}>
      <Table size="small" stickyHeader={alturaMax != null}>
        <TableHead>
          <TableRow>
            {colunas.map((c, i) => (
              <TableCell key={c.label} align={c.alinhar ?? 'left'} sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                {c.valor ? (
                  <TableSortLabel active={idx === i} direction={idx === i ? dir : 'desc'}
                    onClick={() => { if (idx === i) setDir(dir === 'desc' ? 'asc' : 'desc'); else { setIdx(i); setDir('desc') } }}>
                    {c.label}
                  </TableSortLabel>
                ) : c.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {lista.map((l, i) => (
            <TableRow key={i} hover onClick={onLinha ? () => onLinha(l) : undefined} sx={onLinha ? { cursor: 'pointer' } : undefined}>
              {colunas.map((c) => <TableCell key={c.label} align={c.alinhar ?? 'left'} sx={{ whiteSpace: 'nowrap' }}>{c.render(l)}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

/** Seta de linha expansível (▸ gira para baixo quando aberta). */
export function Seta({ aberta }: { aberta: boolean }) {
  return <Typography component="span" color="text.secondary" sx={{ display: 'inline-block', width: 16, lineHeight: 1.6, transition: 'transform .15s', transform: aberta ? 'rotate(90deg)' : 'none', '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }}>▸</Typography>
}

/** `parado` (tiro curto, 26/10/2026): passou da carência sem vender — selo vermelho ao lado do status. */
export function StatusChip({ s, parado }: { s: StatusRitmo; parado?: boolean }) {
  const cor = s === 'ATRAS' ? 'error' : s === 'A_FRENTE' ? 'primary' : s === 'NO_RITMO' ? 'success' : 'default'
  const chip = <Chip size="small" color={cor} variant={s === 'SEM_LEITURA' ? 'outlined' : 'filled'} label={STATUS[s]} />
  if (!parado) return chip
  return <Box component="span" sx={{ display: 'inline-flex', gap: 0.5, flexWrap: 'wrap' }}>{chip}<Chip size="small" color="error" variant="outlined" label="Parado sem venda" /></Box>
}

/** MC (fração) colorida: verde ≥ 10% · laranja 3–10% · vermelho < 3%. */
export function Mc({ v }: { v: number | null | undefined }) {
  if (v == null || !Number.isFinite(v)) return <>—</>
  const cor = v < 0.03 ? 'error.main' : v < 0.10 ? 'warning.main' : 'success.main'
  return <Box component="span" sx={{ color: cor, fontWeight: 600 }}>{pct(v, 1)}</Box>
}

/** Imagem + título com link para o anúncio (texto do marketplace renderizado como texto, link com noopener). */
export function Produto({ a, largura = 300, extra }: { a: Pick<Anuncio, 'titulo' | 'link' | 'imagem' | 'id' | 'item_id' | 'variacao' | 'sku_antigo'>; largura?: number; extra?: ReactNode }) {
  const conteudo = (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', maxWidth: largura, minWidth: Math.min(largura, 240) }}>
      {a.imagem
        ? <Box component="img" src={a.imagem} alt="" loading="lazy" referrerPolicy="no-referrer" sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1, flexShrink: 0, bgcolor: 'action.hover' }} />
        : <Box sx={{ width: 40, height: 40, borderRadius: 1, flexShrink: 0, bgcolor: 'action.hover' }} />}
      <Box sx={{ minWidth: 0, whiteSpace: 'normal' }}>
        <Typography variant="body2" sx={{ lineHeight: 1.3 }}>{a.titulo || '—'}</Typography>
        <Typography variant="caption" color="text.secondary">{a.item_id ?? a.id}{a.variacao ? ` · variação ${a.variacao}` : ''}</Typography>
        {a.sku_antigo && <Typography variant="caption" color="warning.main" display="block">SKU antigo no anúncio: {a.sku_antigo} (trocar pelo novo)</Typography>}
        {extra}
      </Box>
    </Box>
  )
  return a.link
    ? <Link href={a.link} target="_blank" rel="noopener noreferrer" underline="hover" color="inherit" onClick={(e) => e.stopPropagation()}>{conteudo}</Link>
    : conteudo
}

export function Comps({ comps }: { comps: [string, number][] }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 220 }}>
      {comps.map(([s, q]) => <Chip key={s} size="small" variant="outlined" label={q !== 1 ? `${s} ×${q}` : s} />)}
    </Box>
  )
}

export function StatusAnuncio({ a }: { a: Anuncio }) {
  if (a.status === 'ativo') return <Chip size="small" color="success" label="ativo" />
  if (a.status === 'pausado') return <Chip size="small" color="warning" label="pausado" />
  if (a.status === 'vende') return <Chip size="small" variant="outlined" label="pelas vendas" title="Canal sem leitura de anúncio nesta versão: só as vendas" />
  return <Chip size="small" variant="outlined" label="outro status" title={motivoOutro(a)} />
}

export function BotaoAtualizarCatalogos({ quando, ocupado, onClick }: { quando: string | null | undefined; ocupado: boolean; onClick: () => void }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="caption" color="text.secondary">{quando ? `atualizado às ${horaSp(quando)}` : 'ainda não atualizado'}</Typography>
      <Button size="small" variant="outlined" disabled={ocupado} onClick={onClick}>{ocupado ? 'Atualizando…' : 'Atualizar catálogos'}</Button>
    </Stack>
  )
}
